import { ComponentProps, FocusEvent, forwardRef } from 'react';
import { cn, Stack } from '@prenuvo/halo';

import { useTextboxCharacterLimit } from '@/shared/hook';

import { AutoHeightTextArea } from './auto-height-textarea.component';
import { CharacterLimitIndicator } from './character-limit-indicator.component';

type TextboxCharacterLimitTextAreaBaseProps = Omit<
  ComponentProps<typeof AutoHeightTextArea>,
  'maxLength' | 'onBlur' | 'onChange' | 'onFocus' | 'onKeyDown' | 'onPaste' | 'value'
>;

interface TextboxCharacterLimitTextAreaProps extends TextboxCharacterLimitTextAreaBaseProps {
  allowLineBreaks?: boolean;
  maxLength: number;
  onBlur?: ComponentProps<typeof AutoHeightTextArea>['onBlur'];
  onValueChange: (value: string) => void;
  value: string;
}

export const TextboxCharacterLimitTextArea = forwardRef<
  HTMLTextAreaElement,
  TextboxCharacterLimitTextAreaProps
>(
  (
    { allowLineBreaks = false, className, maxLength, onBlur, onValueChange, value, ...props },
    ref,
  ) => {
    const characterLimit = useTextboxCharacterLimit({
      maxLength,
      onValueChange,
      value,
    });

    const handleBlur = (event: FocusEvent<HTMLTextAreaElement>) => {
      characterLimit.onBlur();
      onBlur?.(event);
    };

    return (
      <Stack className="w-full" variant="vertical">
        <AutoHeightTextArea
          {...props}
          className={cn(
            className,
            'disabled:text-semantic-text-disabled disabled:pointer-events-none',
          )}
          maxLength={maxLength}
          onBlur={handleBlur}
          onChange={characterLimit.onChangeWithLimit}
          onFocus={characterLimit.onFocus}
          onKeyDown={allowLineBreaks ? undefined : characterLimit.onKeyDownPreventLineBreak}
          onPaste={characterLimit.onPasteWithLimit}
          ref={ref}
          value={value}
        />
        <CharacterLimitIndicator
          currentLength={characterLimit.currentLength}
          isActive={characterLimit.isFocused}
          maxLength={maxLength}
        />
      </Stack>
    );
  },
);

TextboxCharacterLimitTextArea.displayName = 'TextboxCharacterLimitTextArea';
